import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from '@/components/shell/AppShell';
import { RequireAuth } from '@/components/shell/RequireAuth';
import { Spinner } from '@/components/ui';
import { STALE_TIME } from '@/lib/api/queries';

/**
 * The route tree.
 *
 * Path-for-path the same as RinNET's, which is what lets either portal be pointed at
 * this server — and lets one acceptance script walk both. The asymmetry in the
 * CHUNITHM prefix (`/chuni/v2` in the URL, `chusan` in `user.games`) is deliberate;
 * see `lib/menu.ts`.
 *
 * Game features are lazy: a player who only plays maimai should not download the
 * CHUNITHM user box, and these are the largest pages in the bundle.
 */

const Home = lazy(() => import('@/pages/HomePage'));
const SignIn = lazy(() => import('@/pages/SignInPage'));
const SignUp = lazy(() => import('@/pages/SignUpPage'));
const PasswordReset = lazy(() => import('@/pages/PasswordResetPage'));
const OAuthCallback = lazy(() => import('@/pages/OAuthCallbackPage'));
const Dashboard = lazy(() => import('@/pages/DashboardPage'));
const Account = lazy(() => import('@/pages/AccountPage'));
const Cards = lazy(() => import('@/pages/CardsPage'));
const Keychip = lazy(() => import('@/pages/KeychipPage'));
const Import = lazy(() => import('@/pages/ImportPage'));
const Announcements = lazy(() => import('@/pages/AnnouncementsPage'));
const Contributors = lazy(() => import('@/pages/ContributorsPage'));
const Eula = lazy(() => import('@/pages/EulaPage'));
const Banned = lazy(() => import('@/pages/BannedPage'));
const Admin = lazy(() => import('@/pages/AdminPage'));
const NotFound = lazy(() => import('@/pages/NotFoundPage'));
const ChangePassword = lazy(() => import('@/pages/ChangePasswordPage'));

const ChuniRoutes = lazy(() => import('@/features/chuni/routes'));
const Mai2Routes = lazy(() => import('@/features/mai2/routes'));
const OngekiRoutes = lazy(() => import('@/features/ongeki/routes'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: STALE_TIME,
      // The api client already retries a 401 once, after refreshing. Retrying again
      // here would turn one dead session into four requests per query.
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

function Loading() {
  return (
    <div className="flex justify-center p-12">
      <Spinner className="size-6 text-muted-foreground" />
    </div>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route element={<AppShell />}>
              <Route index element={<Home />} />
              <Route path="sign-in" element={<SignIn />} />
              <Route path="sign-up" element={<SignUp />} />
              <Route path="password-reset" element={<PasswordReset />} />
              <Route path="oauth-callback/:type" element={<OAuthCallback />} />
              <Route path="contributors" element={<Contributors />} />
              <Route path="eula" element={<Eula />} />
              <Route path="banned" element={<Banned />} />

              <Route element={<RequireAuth />}>
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="profile" element={<Account />} />
                {/* Reachable while the server has closed everything else — the
                    account owes a password change and this is the way out. */}
                <Route path="change-password" element={<ChangePassword />} />
                <Route path="cards" element={<Cards />} />
                <Route path="keychip" element={<Keychip />} />
                <Route path="import" element={<Import />} />
                <Route path="announcements" element={<Announcements />} />
                <Route path="announcements/edit" element={<Announcements />} />
                <Route path="admin" element={<Admin />} />

                <Route path="chuni/v2/*" element={<ChuniRoutes />} />
                <Route path="mai2/*" element={<Mai2Routes />} />
                <Route path="ongeki/*" element={<OngekiRoutes />} />
              </Route>

              {/* `/not-found` is a real route because the server's SPA fallback sends
                  unknown deep links to index.html, and a link to it must resolve. */}
              <Route path="not-found" element={<NotFound />} />
              <Route path="*" element={<Navigate to="/not-found" replace />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
